import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import {
  FormGroup,
  FormLabel,
  FormInput,
} from '../../../components/shared/Form/Form.styles';
import {
  ProfileContainer,
  ProfileHeader,
  ProfileCard,
  ProfileSection,
  BookingHistory,
  BookingCard,
  EmptyState,
} from './Profile.styles';

const Profile = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [bookings, setBookings] = useState([]);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
      loadBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadBookings = async () => {
    try {
      const response = await fetch('/api/user/reservations', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setBookings(data.reservations);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback('');

    try {
      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name: formData.name, phone: formData.phone }),
      });
      const result = await response.json();

      if (result.success) {
        setUser(result.user);
        localStorage.setItem('user', JSON.stringify(result.user));
        setEditing(false);
        setFeedback('Perfil actualizado');
      } else {
        setFeedback(result.error || 'Error al actualizar perfil');
      }
    } catch (error) {
      setFeedback('Error de conexión. Intenta nuevamente.');
    }
  };

  const handleCancelBooking = async (booking) => {
    if (!window.confirm(`¿Cancelar la reserva de ${booking.activityName}? El reembolso es automático.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/reservations/${booking.id}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const result = await response.json();

      if (result.success) {
        loadBookings();
      } else {
        alert(result.error || 'No se pudo cancelar la reserva');
      }
    } catch (error) {
      alert('Error de conexión. Intenta nuevamente.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!user) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '64px 20px' }}>
          <h2>Inicia sesión para ver tu perfil</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '12px 0 24px' }}>
            Aquí verás tus datos y el historial de tus reservas.
          </p>
          <Button onClick={() => navigate('/login')}>Iniciar sesión</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ProfileContainer>
        <ProfileHeader>
          <span className="eyebrow">Mi cuenta</span>
          <h1>Hola, {user.name?.split(' ')[0]}</h1>
          <p>Gestiona tu información personal y revisa tus reservas</p>
        </ProfileHeader>

        <ProfileCard>
          <ProfileSection>
            <div className="section-head">
              <h2>Información personal</h2>
              {!editing ? (
                <Button variant="secondary" size="small" onClick={() => setEditing(true)}>
                  Editar
                </Button>
              ) : (
                <div className="edit-actions">
                  <Button variant="ghost" size="small" onClick={() => setEditing(false)}>
                    Cancelar
                  </Button>
                  <Button size="small" onClick={handleSubmit}>
                    Guardar
                  </Button>
                </div>
              )}
            </div>

            {feedback && <p className="feedback">{feedback}</p>}

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel>Nombre completo</FormLabel>
                <FormInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Email</FormLabel>
                <FormInput type="email" name="email" value={formData.email} disabled />
              </FormGroup>

              <FormGroup>
                <FormLabel>Teléfono</FormLabel>
                <FormInput
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </FormGroup>
            </form>
          </ProfileSection>

          <ProfileSection>
            <h2>Cuenta</h2>
            <div className="account-actions">
              <Button variant="danger" fullWidth onClick={logout}>
                Cerrar sesión
              </Button>
            </div>
          </ProfileSection>
        </ProfileCard>

        <BookingHistory>
          <h2>Mis reservas</h2>
          {bookings.length > 0 ? (
            bookings.map(booking => (
              <BookingCard key={booking.id}>
                <div className="booking-header">
                  <div>
                    <h3>{booking.activityName}</h3>
                    <StatusBadge status={booking.status} />
                  </div>
                  <div className="booking-amount">
                    ${booking.finalAmount ? parseFloat(booking.finalAmount).toLocaleString() : 0}
                  </div>
                </div>
                <div className="booking-details">
                  <p>
                    📅 {new Date(`${booking.date}T12:00:00`).toLocaleDateString()} · 🕐 {booking.time} · 👥 {booking.quantity} persona(s)
                  </p>
                  <p className="booking-id">ID: {booking.id}</p>
                </div>
                {booking.status === 'pending' && (
                  <div className="booking-actions">
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => navigate('/booking-confirmed', {
                        state: { reservationData: booking, qrCode: booking.qrCode },
                      })}
                    >
                      Ver QR
                    </Button>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => handleCancelBooking(booking)}
                    >
                      Cancelar reserva
                    </Button>
                  </div>
                )}
              </BookingCard>
            ))
          ) : (
            <EmptyState>
              <h3>No tienes reservas todavía</h3>
              <p>Explora las actividades y reserva tu primera experiencia</p>
              <Button onClick={() => navigate('/search')}>Buscar actividades</Button>
            </EmptyState>
          )}
        </BookingHistory>
      </ProfileContainer>
    </Layout>
  );
};

export default Profile;
